import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CommissionRuleCreateSchema,
  CommissionRuleUpdateSchema,
  type CommissionRuleCreateRequest,
  type CommissionRuleItem,
  type CommissionRuleUpdateRequest,
  type ServiceItem,
  type UserDirectoryItem,
} from '@crm/shared';
import { extractApiError } from '../../lib/api';
import { useCommissionRuleCreate, useCommissionRuleUpdate } from './api';

type Props = {
  open: boolean;
  rule: CommissionRuleItem | null; // null = create
  employees: UserDirectoryItem[];
  services: ServiceItem[];
  prefillUserId?: number | null;
  onClose: () => void;
};

type FormState = {
  userId: string;
  serviceId: string; // '' = bazowa
  percent: string;
  validFrom: string;
  validTo: string; // '' = bez końca
};

const todayIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const EMPTY = (prefillUserId?: number | null): FormState => ({
  userId: prefillUserId ? String(prefillUserId) : '',
  serviceId: '',
  percent: '30.00',
  validFrom: todayIso(),
  validTo: '',
});

function fromRule(r: CommissionRuleItem): FormState {
  return {
    userId: String(r.userId),
    serviceId: r.serviceId ? String(r.serviceId) : '',
    percent: r.percent,
    validFrom: r.validFrom,
    validTo: r.validTo ?? '',
  };
}

export function CommissionRuleModal({
  open,
  rule,
  employees,
  services,
  prefillUserId,
  onClose,
}: Props): ReactNode {
  const { t } = useTranslation();
  const isEdit = rule !== null;
  const create = useCommissionRuleCreate();
  const update = useCommissionRuleUpdate(rule?.id ?? 0);

  const [form, setForm] = useState<FormState>(() => EMPTY(prefillUserId));
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setSubmitError(null);
    setForm(rule ? fromRule(rule) : EMPTY(prefillUserId));
  }, [open, rule, prefillUserId]);

  if (!open) return null;

  const submitting = create.isPending || update.isPending;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setErrors({});

    if (isEdit && rule) {
      const candidate: Record<string, unknown> = {};
      // W edycji nie zmieniamy klucza (userId, serviceId) — tylko percent/daty.
      if (form.percent !== rule.percent) candidate.percent = form.percent;
      if (form.validFrom !== rule.validFrom) candidate.validFrom = form.validFrom;
      const nextValidTo = form.validTo.trim() === '' ? null : form.validTo;
      if (nextValidTo !== rule.validTo) candidate.validTo = nextValidTo;
      if (Object.keys(candidate).length === 0) {
        onClose();
        return;
      }
      const parsed = CommissionRuleUpdateSchema.safeParse(candidate);
      if (!parsed.success) {
        const fld: Partial<Record<keyof FormState, string>> = {};
        for (const issue of parsed.error.issues) {
          const path = issue.path[0];
          if (path === 'percent' || path === 'validFrom' || path === 'validTo') {
            fld[path] = issue.message;
          }
        }
        setErrors(fld);
        return;
      }
      try {
        await update.mutateAsync(parsed.data as CommissionRuleUpdateRequest);
        onClose();
      } catch (err) {
        setSubmitError(extractApiError(err));
      }
      return;
    }

    // CREATE
    const candidate: Record<string, unknown> = {
      userId: Number(form.userId),
      serviceId: form.serviceId === '' ? null : Number(form.serviceId),
      percent: form.percent,
      validFrom: form.validFrom,
      validTo: form.validTo.trim() === '' ? null : form.validTo,
    };
    const parsed = CommissionRuleCreateSchema.safeParse(candidate);
    if (!parsed.success) {
      const fld: Partial<Record<keyof FormState, string>> = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path[0];
        if (
          path === 'userId' ||
          path === 'serviceId' ||
          path === 'percent' ||
          path === 'validFrom' ||
          path === 'validTo'
        ) {
          fld[path as keyof FormState] = issue.message;
        }
      }
      setErrors(fld);
      return;
    }
    try {
      await create.mutateAsync(parsed.data as CommissionRuleCreateRequest);
      onClose();
    } catch (err) {
      setSubmitError(extractApiError(err));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-zinc-900">
            {isEdit ? t('commissions.rules.form.titleEdit') : t('commissions.rules.form.titleCreate')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
            aria-label="close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-zinc-700">
              {t('commissions.rules.form.employee')}
            </label>
            <select
              value={form.userId}
              onChange={(e) => setForm((f) => ({ ...f, userId: e.target.value }))}
              disabled={isEdit}
              className="input"
            >
              <option value="">{t('commissions.rules.form.employeePlaceholder')}</option>
              {employees.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.firstName} {u.lastName}
                </option>
              ))}
            </select>
            {errors.userId && <p className="mt-1 text-xs text-rose-600">{errors.userId}</p>}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-zinc-700">
              {t('commissions.rules.form.service')}
            </label>
            <select
              value={form.serviceId}
              onChange={(e) => setForm((f) => ({ ...f, serviceId: e.target.value }))}
              disabled={isEdit}
              className="input"
            >
              <option value="">{t('commissions.rules.form.baseRule')}</option>
              {services
                .filter((s) => s.active)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.category ? ` (${s.category})` : ''}
                  </option>
                ))}
            </select>
            <p className="mt-1 text-xs text-zinc-500">
              {t('commissions.rules.form.serviceHint')}
            </p>
            {errors.serviceId && <p className="mt-1 text-xs text-rose-600">{errors.serviceId}</p>}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-zinc-700">
              {t('commissions.rules.form.percent')}
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={form.percent}
                onChange={(e) => setForm((f) => ({ ...f, percent: e.target.value }))}
                className="input pr-8"
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">
                %
              </span>
            </div>
            {errors.percent && <p className="mt-1 text-xs text-rose-600">{errors.percent}</p>}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-zinc-700">
                {t('commissions.rules.form.validFrom')}
              </label>
              <input
                type="date"
                value={form.validFrom}
                onChange={(e) => setForm((f) => ({ ...f, validFrom: e.target.value }))}
                className="input"
              />
              {errors.validFrom && (
                <p className="mt-1 text-xs text-rose-600">{errors.validFrom}</p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-zinc-700">
                {t('commissions.rules.form.validTo')}
              </label>
              <input
                type="date"
                value={form.validTo}
                onChange={(e) => setForm((f) => ({ ...f, validTo: e.target.value }))}
                className="input"
              />
              <p className="mt-1 text-xs text-zinc-500">
                {t('commissions.rules.form.validToHint')}
              </p>
              {errors.validTo && <p className="mt-1 text-xs text-rose-600">{errors.validTo}</p>}
            </div>
          </div>

          {submitError && (
            <div className="rounded-md border border-rose-300 bg-rose-50 p-2 text-sm text-rose-700">
              {submitError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
